import { forwardRef, useEffect, useId, useImperativeHandle, useRef, type ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface CheckboxProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Mixed state, e.g. "select all" with some rows selected. */
  indeterminate?: boolean;
  disabled?: boolean;
  description?: ReactNode;
  error?: string;
  /** Visually hide the label (it stays the accessible name). */
  hideLabel?: boolean;
  id?: string;
  className?: string;
}

/**
 * Checkbox - a themed box over a real input, so keyboard and assistive tech behave natively.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, checked, onChange, indeterminate = false, disabled, description, error, hideLabel, id, className },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const descId = `${inputId}-desc`;
  const errId = `${inputId}-err`;
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const describedBy = [description ? descId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  const filled = checked || indeterminate;

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        className={cn(
          'group inline-flex items-center gap-3 min-h-[44px]',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <span className="relative inline-flex h-5 w-5 shrink-0">
          <input
            ref={innerRef}
            id={inputId}
            type="checkbox"
            checked={Boolean(checked)}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            onChange={(event) => {
              if (!disabled) onChange(event.target.checked);
            }}
            className="peer absolute -inset-3 h-11 w-11 cursor-[inherit] opacity-0"
          />
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-focus-ring peer-focus-visible:ring-offset-2',
              filled ? 'bg-accent-primary border-accent-primary text-text-on-accent' : 'bg-bg-tertiary border-border-strong group-hover:border-accent-primary',
              error && 'border-semantic-error',
            )}
          >
            {indeterminate ? <Minus size={14} strokeWidth={3} /> : checked ? <Check size={14} strokeWidth={3} /> : null}
          </span>
        </span>
        <span className={cn('text-sm text-text-primary', hideLabel && 'sr-only')}>{label}</span>
      </label>
      {description && <p id={descId} className="ml-8 -mt-2 text-xs text-text-muted">{description}</p>}
      {error && <p id={errId} className="ml-8 text-xs text-semantic-error">{error}</p>}
    </div>
  );
});
